<template>
  <div class="layout" :class="{ 'side-collapsed': sideCollapsed }">
    <aside class="app-side">
      <div class="side-brand">
        <img
          v-if="companyLogoSrc"
          class="logo-img"
          :src="companyLogoSrc"
          :title="tenantName"
          alt=""
        />
        <img
          v-else
          class="logo-img"
          src="/brand-logo.png?v=asset2"
          title="华锐资产云"
          alt="华锐资产云"
        />
        <div v-show="!sideCollapsed" class="brand-text">
          <div class="brand-name">华锐资产云</div>
        </div>
        <button
          type="button"
          class="side-collapse-btn"
          :title="sideCollapsed ? '展开菜单' : '收起菜单'"
          @click="toggleSide"
        >
          <i :class="sideCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'" />
        </button>
      </div>

      <nav class="side-nav">
        <template v-for="block in navBlocks">
          <!-- 顶级单页 -->
          <router-link
            v-if="block.kind === 'link'"
            :key="'t-' + block.id"
            :to="block.path"
            class="side-top-link"
            :class="{ active: isActive(block.path) }"
            :title="block.name"
          >
            <i :class="block.icon || 'el-icon-menu'" class="side-group-icon" />
            <span v-show="!sideCollapsed">{{ block.name }}</span>
          </router-link>

          <!-- 一级目录 + 二级菜单 -->
          <div
            v-else
            :key="'s-' + block.id"
            class="side-section"
          >
            <button
              type="button"
              class="side-group"
              :class="{ open: isExpanded(block.id), active: sectionHasActive(block) }"
              :title="block.name"
              @click="onSectionClick(block.id)"
            >
              <span class="side-group-left">
                <i :class="block.icon || 'el-icon-folder'" class="side-group-icon" />
                <span v-show="!sideCollapsed" class="side-group-label">{{ block.name }}</span>
              </span>
              <i v-show="!sideCollapsed" class="el-icon-arrow-right side-caret" />
            </button>
            <div v-show="!sideCollapsed && isExpanded(block.id)" class="side-children">
              <template v-for="item in block.children">
                <div v-if="item.kind === 'group'" :key="'g-' + item.id" class="side-sub-group">
                  <div class="side-sub-label">{{ item.name }}</div>
                  <router-link
                    v-for="sub in item.children"
                    :key="sub.id"
                    :to="sub.path"
                    class="side-item side-item-nested"
                    :class="{ active: isActive(sub.path) }"
                    :title="sub.name"
                  >{{ sub.name }}</router-link>
                </div>
                <router-link
                  v-else
                  :key="'i-' + item.id"
                  :to="item.path"
                  class="side-item"
                  :class="{ active: isActive(item.path) }"
                  :title="item.name"
                >{{ item.name }}</router-link>
              </template>
            </div>
          </div>
        </template>
      </nav>
    </aside>

    <main class="workspace">
      <header class="header" :class="{ 'tour-active': tourStep > 0 }">
        <div class="page-title">{{ pageTitle }}</div>
        <div class="header-right">
          <div v-if="showHelpEntry" class="header-help" ref="headerHelp">
            <button
              ref="helpBtn"
              type="button"
              class="header-help-btn"
              :class="{ active: isActive('/help/biz-flow'), tour: tourStep === 1 }"
              @click="goHelp"
            >
              <i class="el-icon-question" />
              <span>使用帮助</span>
            </button>
            <button
              ref="consultBtn"
              type="button"
              class="header-help-btn"
              :class="{ active: isActive('/help/consult-ticket'), tour: tourStep === 2 }"
              @click="goConsult"
            >
              <i class="el-icon-s-order" />
              <span>咨询工单</span>
            </button>
          </div>
          <span v-if="tenantName" class="header-tenant" :title="tenantPlanTitle">
            {{ tenantName }}<template v-if="planName"> {{ planName }}</template><template v-if="accountUsage"> （{{ accountUsage }}）</template>
          </span>
          <el-tag v-if="tenantStatus" size="mini" :type="statusTagType">{{ statusLabel }}</el-tag>
          <span v-if="usageEndsLabel" class="usage-ends">至 {{ usageEndsLabel }}</span>
          <el-dropdown trigger="click" @command="onUserCommand">
            <div class="header-user" title="账号菜单">
              <img v-if="avatarSrc" class="header-avatar-img" :src="avatarSrc" alt="" />
              <span v-else class="header-avatar">{{ shortName }}</span>
              <span class="header-user-name">{{ userLabel || '用户' }}</span>
              <i class="el-icon-arrow-down header-user-caret" />
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">个人资料</el-dropdown-item>
              <el-dropdown-item command="password">修改密码</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <!-- 首次引导：指向顶栏帮助入口 -->
      <div v-if="tourStep > 0" class="help-tour-mask">
        <div
          class="help-tour-bubble"
          :style="tourBubbleStyle"
        >
          <div class="help-tour-title">{{ tourStep === 1 ? '使用帮助在这里' : '有问题可提工单' }}</div>
          <div class="help-tour-desc">
            <template v-if="tourStep === 1">
              不清楚怎么用时，点这里查看「业务流程」总览和「操作手册」逐步说明。
            </template>
            <template v-else>
              使用中遇到问题，可在这里提交咨询工单，平台会跟进回复。
            </template>
          </div>
          <div class="help-tour-actions">
            <el-button v-if="tourStep === 1" type="primary" size="mini" @click="nextTour">下一步</el-button>
            <el-button v-else type="primary" size="mini" @click="finishTour">知道了</el-button>
          </div>
        </div>
      </div>
      <tags-view />
      <el-alert
        v-if="isExpired"
        class="expire-banner"
        type="warning"
        show-icon
        :closable="false"
        title="试用或订阅已到期，系统为只读状态。请到「套餐订阅」提交对公转账认款续费。"
      >
        <el-button type="text" @click="goSubscription">去续费</el-button>
      </el-alert>
      <div class="page-body">
        <keep-alive :include="cachedViews">
          <router-view :key="viewKey" />
        </keep-alive>
      </div>
    </main>

    <el-dialog title="个人信息" :visible.sync="profileVisible" width="460px" append-to-body>
      <el-form :model="profileForm" label-width="90px">
        <el-form-item label="头像">
          <div class="profile-avatar-row">
            <img v-if="profileForm.avatarUrl" class="profile-avatar-preview" :src="profileForm.avatarUrl" alt="" />
            <span v-else class="profile-avatar-fallback">{{ shortName }}</span>
            <el-upload
              action="#"
              :http-request="elUploadHttpRequest"
              :show-file-list="false"
              :before-upload="beforeAvatarUpload"
              :on-success="onAvatarUpload"
              :on-error="() => { this.avatarUploading = false }"
              accept="image/*"
            >
              <el-button size="small" type="primary" plain :loading="avatarUploading">上传头像</el-button>
            </el-upload>
          </div>
        </el-form-item>
        <el-form-item label="登录账号">
          <el-input :value="user.username" disabled />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="profileForm.nickname" maxlength="32" placeholder="显示名称" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="profileVisible=false">取消</el-button>
        <el-button type="primary" :loading="profileSaving" @click="saveProfile">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="修改密码" :visible.sync="pwdVisible" width="420px" append-to-body>
      <el-form :model="pwdForm" label-width="90px">
        <el-form-item label="原密码" required>
          <el-input v-model="pwdForm.oldPassword" type="password" show-password />
        </el-form-item>
        <el-form-item label="新密码" required>
          <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="至少6位，含字母和数字" />
        </el-form-item>
        <el-form-item label="确认密码" required>
          <el-input v-model="pwdForm.confirm" type="password" show-password />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="pwdVisible=false">取消</el-button>
        <el-button type="primary" :loading="pwdSaving" @click="savePassword">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchNav, fetchMe, fetchButtonPerms, changePassword, updateProfile } from '../api/auth'
import { firstMenuPath, isSupplierUser } from '../utils/nav'
import { elUploadHttpRequest } from '../utils/ossUpload'
import { passwordError } from '../utils/password'
import { accountUsageText, assetUsageText, planNameOf } from '../utils/plan'
import TagsView from '../components/TagsView.vue'
import store from '../store'

const STORAGE_KEY = 'jz_side_collapsed'
const HELP_TOUR_KEY = 'jz_help_header_tour_seen'
/** 侧栏不再展示：使用帮助 / 业务流程 / 咨询工单（改顶栏入口） */
const HEADER_HELP_MENU_IDS = new Set([96, 97, 98])

export default {
  name: 'Layout',
  components: { TagsView },
  data() {
    let collapsed = false
    try {
      collapsed = localStorage.getItem(STORAGE_KEY) === '1'
    } catch (e) { /* ignore */ }
    return {
      sideCollapsed: collapsed,
      menus: [],
      expanded: {},
      pwdVisible: false,
      pwdSaving: false,
      pwdForm: { oldPassword: '', newPassword: '', confirm: '' },
      profileVisible: false,
      profileSaving: false,
      avatarUploading: false,
      profileForm: { nickname: '', avatarUrl: '' },
      tourStep: 0,
      tourBubbleStyle: {}
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    showHelpEntry() {
      return !isSupplierUser(this.user)
    },
    tenantName() {
      return this.user.tenantName || '未命名企业'
    },
    planName() {
      return planNameOf(this.user)
    },
    accountUsage() {
      if (this.user.maxUsers == null && this.user.userCount == null) return ''
      return accountUsageText(this.user)
    },
    assetUsage() {
      return assetUsageText(this.user)
    },
    tenantPlanTitle() {
      const parts = [this.tenantName]
      if (this.planName) parts.push(this.planName)
      const usage = [this.accountUsage, this.assetUsage].filter(Boolean).join('，')
      if (usage) parts.push(`（${usage}）`)
      return parts.join(' ')
    },
    tenantStatus() {
      return this.user.tenantStatus || ''
    },
    isExpired() {
      return this.tenantStatus === 'EXPIRED'
    },
    statusTagType() {
      if (this.tenantStatus === 'EXPIRED') return 'danger'
      if (this.tenantStatus === 'TRIAL') return 'warning'
      if (this.tenantStatus === 'ACTIVE') return 'success'
      return 'info'
    },
    statusLabel() {
      const map = { TRIAL: '试用中', ACTIVE: '已开通', EXPIRED: '已到期', DISABLED: '已禁用' }
      return map[this.tenantStatus] || this.tenantStatus
    },
    usageEndsLabel() {
      const raw = this.user.usageEndsAt || this.user.trialEndsAt
      if (!raw) return ''
      const s = String(raw)
      // yyyy-MM-dd HH:mm:ss → yyyy-MM-dd
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    userLabel() {
      return this.user.nickname || this.user.username || ''
    },
    shortName() {
      const n = this.userLabel
      return n ? n.slice(0, 1) : 'U'
    },
    avatarSrc() {
      return this.user.avatarUrl || ''
    },
    companyLogoSrc() {
      return this.user.companyLogoUrl || ''
    },
    topLinks() {
      return this.navBlocks.filter(b => b.kind === 'link')
    },
    sections() {
      return this.navBlocks.filter(b => b.kind === 'section')
    },
    navBlocks() {
      const blocks = []
      ;(this.menus || []).forEach(m => {
        if (!m || HEADER_HELP_MENU_IDS.has(Number(m.id))) return
        if (m.type === 1 && m.path && !m.children) {
          blocks.push({
            kind: 'link',
            id: m.id,
            name: m.name,
            path: m.path,
            icon: m.icon
          })
          return
        }
        if (m.type === 0) {
          const children = this.flattenSectionChildren(m.children || [])
          if (children.length) {
            blocks.push({
              kind: 'section',
              id: m.id,
              name: m.name,
              icon: m.icon,
              children
            })
          }
        }
      })
      return blocks
    },
    pageTitle() {
      const trail = this.menuTrail(this.$route.path)
      if (trail.length) return trail.join(' / ')
      if (this.$route.meta && this.$route.meta.title) return this.$route.meta.title
      return '首页'
    },
    cachedViews() {
      return this.$store.state.tagsView.cachedViews
    },
    viewKey() {
      return this.$route.fullPath + '::' + this.$store.state.tagsView.refreshKey
    }
  },
  watch: {
    menus: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.initExpand())
      }
    },
    '$route.path'() {
      this.ensureActiveExpanded()
    }
  },
  created() {
    this.load()
  },
  methods: {
    elUploadHttpRequest,
    async load() {
      try {
        const [navRes, meRes, permRes] = await Promise.all([
          fetchNav(),
          fetchMe(),
          fetchButtonPerms().catch(() => ({ data: ['*'] }))
        ])
        this.menus = navRes.data || []
        sessionStorage.setItem('permissions', JSON.stringify(permRes.data || ['*']))
        this.$store.commit('patchUser', meRes.data || {})
        this.redirectSupplierFromHome()
        this.$nextTick(() => this.maybeStartHelpTour())
      } catch (e) { /* auth interceptor */ }
    },
    goHelp() {
      this.$router.push('/help/biz-flow')
    },
    goConsult() {
      this.$router.push('/help/consult-ticket')
    },
    maybeStartHelpTour() {
      if (!this.showHelpEntry) return
      try {
        if (localStorage.getItem(HELP_TOUR_KEY) === '1') return
      } catch (e) { /* ignore */ }
      this.tourStep = 1
      this.$nextTick(() => this.positionTourBubble())
    },
    positionTourBubble() {
      const refName = this.tourStep === 1 ? 'helpBtn' : 'consultBtn'
      const el = this.$refs[refName]
      const target = Array.isArray(el) ? el[0] : el
      if (!target || !target.getBoundingClientRect) {
        this.tourBubbleStyle = { top: '56px', right: '180px' }
        return
      }
      const rect = target.getBoundingClientRect()
      const bubbleW = 280
      let left = rect.left + rect.width / 2 - bubbleW / 2
      left = Math.max(12, Math.min(left, window.innerWidth - bubbleW - 12))
      this.tourBubbleStyle = {
        top: (rect.bottom + 10) + 'px',
        left: left + 'px',
        width: bubbleW + 'px'
      }
    },
    nextTour() {
      this.tourStep = 2
      this.$nextTick(() => this.positionTourBubble())
    },
    finishTour() {
      this.markTourSeen()
      this.tourStep = 0
    },
    skipTour() {
      this.markTourSeen()
      this.tourStep = 0
    },
    markTourSeen() {
      try {
        localStorage.setItem(HELP_TOUR_KEY, '1')
      } catch (e) { /* ignore */ }
    },
    redirectSupplierFromHome() {
      if (!isSupplierUser(this.user)) return
      const p = this.$route.path
      if (p !== '/' && p !== '/home') return
      const target = firstMenuPath(this.menus)
      if (target && target !== p) {
        this.$router.replace(target)
      }
    },
    toggleSide() {
      this.sideCollapsed = !this.sideCollapsed
      try {
        localStorage.setItem(STORAGE_KEY, this.sideCollapsed ? '1' : '0')
      } catch (e) { /* ignore */ }
    },
    isActive(path) {
      if (!path) return false
      // 精确匹配；禁止用 /project 前缀命中 /project/stages
      return this.$route.path === path
    },
    flattenSectionChildren(children) {
      const out = []
      ;(children || []).forEach(c => {
        if (!c) return
        if (c.type === 1 && c.path) {
          out.push({ kind: 'item', id: c.id, name: c.name, path: c.path, icon: c.icon })
        } else if (c.type === 0) {
          const kids = (c.children || []).filter(x => x && x.type === 1 && x.path)
          if (kids.length) {
            out.push({
              kind: 'group',
              id: c.id,
              name: c.name,
              children: kids.map(x => ({ id: x.id, name: x.name, path: x.path, icon: x.icon }))
            })
          }
        }
      })
      return out
    },
    sectionLeafItems(section) {
      const out = []
      ;(section.children || []).forEach(c => {
        if (c && c.kind === 'group') out.push(...(c.children || []))
        else if (c && c.path) out.push(c)
      })
      return out
    },
    matchMenuItem(path) {
      for (const item of this.topLinks) {
        if (item.path === path) return item
      }
      for (const section of this.sections) {
        for (const item of this.sectionLeafItems(section)) {
          if (item.path === path) return item
        }
      }
      return null
    },
    /** 按菜单树解析完整路径名称，如：基础资料 / 供应商 */
    menuTrail(path) {
      if (!path) return []
      const walk = (nodes, parents) => {
        for (const node of nodes || []) {
          if (!node) continue
          const names = parents.concat(node.name || [])
          if (node.type === 1 && node.path === path) {
            return names
          }
          if (node.children && node.children.length) {
            const hit = walk(node.children, names)
            if (hit.length) return hit
          }
        }
        return []
      }
      return walk(this.menus, [])
    },
    sectionHasActive(section) {
      return this.sectionLeafItems(section).some(c => this.isActive(c.path))
    },
    isExpanded(sectionId) {
      return !!this.expanded[sectionId]
    },
    toggle(sectionId) {
      this.$set(this.expanded, sectionId, !this.expanded[sectionId])
    },
    onSectionClick(sectionId) {
      if (this.sideCollapsed) {
        this.sideCollapsed = false
        try {
          localStorage.setItem(STORAGE_KEY, '0')
        } catch (e) { /* ignore */ }
        this.$nextTick(() => {
          this.$set(this.expanded, sectionId, true)
        })
        return
      }
      this.toggle(sectionId)
    },
    initExpand() {
      const next = {}
      this.sections.forEach(section => {
        // 默认收起；仅展开含当前页的目录
        next[section.id] = this.sectionHasActive(section)
      })
      this.expanded = next
    },
    ensureActiveExpanded() {
      this.sections.forEach(section => {
        if (this.sectionHasActive(section)) {
          this.$set(this.expanded, section.id, true)
        }
      })
    },
    logout() {
      this.$store.commit('tagsView/CLEAR_TAGS')
      this.$store.commit('logout')
      this.$router.replace('/login')
    },
    onUserCommand(cmd) {
      if (cmd === 'profile') this.openProfile()
      else if (cmd === 'password') this.pwdVisible = true
      else if (cmd === 'logout') this.logout()
    },
    goSubscription() {
      this.$router.push('/sys/subscription')
    },
    openProfile() {
      this.profileForm = {
        nickname: this.user.nickname || '',
        avatarUrl: this.user.avatarUrl || ''
      }
      this.profileVisible = true
    },
    beforeAvatarUpload(file) {
      const ok = file && file.type && file.type.indexOf('image/') === 0
      if (!ok) {
        this.$message.warning('请上传图片文件')
        return false
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('头像请小于 5MB')
        return false
      }
      this.avatarUploading = true
      return true
    },
    onAvatarUpload(res) {
      this.avatarUploading = false
      const data = (res && res.data) || {}
      if (res && res.code && res.code !== 200) {
        this.$message.error(res.message || '上传失败')
        return
      }
      const url = data.url || ''
      if (!url) {
        this.$message.error('上传失败')
        return
      }
      this.profileForm.avatarUrl = url
      this.$message.success('头像已上传，请保存')
    },
    async saveProfile() {
      this.profileSaving = true
      try {
        const res = await updateProfile({
          nickname: this.profileForm.nickname,
          avatarUrl: this.profileForm.avatarUrl
        })
        this.$store.commit('patchUser', res.data || {})
        this.$message.success('资料已保存')
        this.profileVisible = false
      } finally {
        this.profileSaving = false
      }
    },
    async savePassword() {
      const f = this.pwdForm
      if (!f.oldPassword || !f.newPassword) return this.$message.warning('请填写原密码和新密码')
      const err = passwordError(f.newPassword)
      if (err) return this.$message.warning(err)
      if (f.newPassword !== f.confirm) return this.$message.warning('两次输入的新密码不一致')
      this.pwdSaving = true
      try {
        await changePassword({ oldPassword: f.oldPassword, newPassword: f.newPassword })
        this.$message.success('密码已修改，请重新登录')
        this.pwdVisible = false
        this.pwdForm = { oldPassword: '', newPassword: '', confirm: '' }
        this.logout()
      } finally {
        this.pwdSaving = false
      }
    }
  }
}
</script>

<style scoped>
.layout {
  display: flex;
  height: 100%;
  min-height: 100%;
  background: #fff;
}
.app-side {
  width: 196px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  transition: width 0.18s ease;
  min-height: 0;
}
.layout.side-collapsed .app-side {
  width: 52px;
}
.side-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  box-sizing: border-box;
  padding: 0 10px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.logo {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  margin: 0; /* 覆盖 platform.css 全局 .logo 的下边距，避免与文字底对齐错位 */
}
.logo-img {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.brand-text {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  height: 28px;
}
.brand-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.side-collapse-btn {
  width: 26px;
  height: 26px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
}
.side-collapse-btn:hover {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}
.layout.side-collapsed .side-brand {
  justify-content: center;
  gap: 0;
  height: 48px;
  padding: 0 4px;
  position: relative;
}
.layout.side-collapsed .side-collapse-btn {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 18px;
  height: 18px;
  font-size: 10px;
}
.side-nav {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.layout.side-collapsed .side-nav {
  padding: 8px 6px;
  align-items: stretch;
}
.side-top-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  color: #0f172a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
}
.side-top-link:hover {
  background: #eff6ff;
  color: #1d4ed8;
}
.side-top-link.active {
  background: #dbeafe;
  color: #1e3a8a;
}
.layout.side-collapsed .side-top-link {
  justify-content: center;
  padding: 10px 0;
}
.side-section + .side-section {
  margin-top: 2px;
}
.side-group {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin: 0;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
}
.side-group:hover {
  background: #eff6ff;
  color: #1d4ed8;
}
.side-group.active {
  color: #1e40af;
}
.layout.side-collapsed .side-group {
  justify-content: center;
  padding: 10px 0;
}
.layout.side-collapsed .side-group-left {
  flex: 0;
  justify-content: center;
}
.side-group-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.side-group-icon {
  font-size: 16px;
  color: inherit;
  flex-shrink: 0;
  width: 18px;
  text-align: center;
}
.side-group-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.side-caret {
  flex-shrink: 0;
  font-size: 12px;
  color: #94a3b8;
  transition: transform 0.15s ease;
}
.side-group.open .side-caret {
  transform: rotate(90deg);
}
.side-children {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0 4px;
}
.side-item {
  display: block;
  /* 与一级菜单文字左对齐：10(组padding) + 18(图标) + 8(间距) */
  padding: 7px 10px 7px 36px;
  border-radius: 8px;
  color: #475569;
  text-decoration: none;
  font-size: 13px;
  line-height: 1.35;
}
.side-item:hover {
  background: #eff6ff;
  color: #1d4ed8;
}
.side-item.active {
  background: #dbeafe;
  color: #1e3a8a;
  font-weight: 600;
}
.side-sub-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.side-sub-label {
  padding: 6px 10px 2px 36px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}
.side-item-nested {
  padding-left: 48px;
}
.profile-avatar-row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.profile-avatar-preview,
.profile-avatar-fallback {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 22px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.workspace {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* 覆盖 platform.css 全局 .workspace { padding: 22px }，标题栏贴顶 */
  padding: 0;
  overflow: hidden;
  background: #fff;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  box-sizing: border-box;
  padding: 0 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}
.header.tour-active {
  position: relative;
  z-index: 10002;
}
.page-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 16px;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.header-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 4px;
}
.header-help-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
  line-height: 1;
}
.header-help-btn i {
  font-size: 14px;
}
.header-help-btn:hover {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}
.header-help-btn.active {
  background: #dbeafe;
  color: #1e3a8a;
  border-color: #93c5fd;
}
.header-help-btn.tour {
  position: relative;
  z-index: 10002;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.35);
  border-color: #2563eb;
  color: #1d4ed8;
  background: #fff;
}
.help-tour-mask {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(15, 23, 42, 0.28);
}
.help-tour-bubble {
  position: fixed;
  z-index: 10001;
  box-sizing: border-box;
  padding: 14px 16px 12px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  border: 1px solid #e2e8f0;
}
.help-tour-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.help-tour-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 12px;
}
.help-tour-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
}
.header-tenant {
  max-width: 420px;
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.usage-ends {
  font-size: 12px;
  color: #64748b;
}
.expire-banner {
  margin: 0;
  border-radius: 0;
  border-left: none;
  border-right: none;
}
.expire-banner .el-button {
  padding: 0;
  margin-left: 8px;
}
.header-user {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 200px;
  padding: 4px 8px 4px 4px;
  border-radius: 20px;
  cursor: pointer;
  color: #334155;
  outline: none;
}
.header-user:hover {
  background: #f1f5f9;
}
.header-avatar,
.header-avatar-img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  background: #eff6ff;
}
.header-avatar {
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.header-user-name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}
.header-user-caret {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}
.page-body {
  flex: 1;
  min-height: 0;
  padding: 10px 12px 12px;
  overflow: auto;
  background: #fff;
}
</style>
